import React, { useRef, useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import moment from 'moment';
import { SelectBox } from 'devextreme-react';
import TenantService from '../../../api/tenant.service';
import AssetsService from '../../../api/assets.service';
import { LoadPanel as LoadingPanel2 } from 'devextreme-react/load-panel';
import './mechanical.css';
import FeaturesService from 'api/features.service';
import { useTheme } from 'contexts/theme';

const MRP_SOLUTIONS_TENANT_ID = '0fcc4e26-8d5c-409c-a60c-a4a87c20d416';
const Plattsburgh_TENANT_ID = 'f51ff9cb-5b92-4730-8844-159f1df4ee6f';

export default function SyncMechanicalCharts({ assetIds, startDate, endDate, features, assetColorMap = {} }) {
  const [loading, setLoading] = useState(false);
  const tempChartRef = useRef(null);
  const impactChartRef = useRef(null);
  const impactSpaceChartRef = useRef(null);
  const containerRef = useRef(null);
  const chartRefs = [useRef(null), useRef(null), useRef(null)];
  const [temperatureData, setTemperatureData] = useState([]);
  const [impactData, setImpactData] = useState([]);
  const [eventData, setCyclesData] = useState([]);
  const [grouping, setGrouping] = useState('Monthly');
  const tenant = TenantService.getTenant();
  const user = localStorage.getItem("user")
  const isFahrenheit = !JSON.parse(user).temperatureUnitMetric;
  // const isFahrenheit = tenant === MRP_SOLUTIONS_TENANT_ID || tenant === Plattsburgh_TENANT_ID;
  const tempUnit = isFahrenheit ? '°F' : '°C';
  const [isCycleTelemetryVisible, setIsCycleTelemetryVisible] = useState(false);
  const [isTemperatureTelemetryVisible, setIsTemperatureTelemetryVisible] = useState(false);
  const [isImpactTelemetryVisible, setIsImpactTelemetryVisible] = useState(false);
  const { theme } = useTheme();
  const axisTextColor = theme === 'dark' ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)';
  const noDataColor = theme === 'dark' ? '#bbb' : '#999';
  // Subtle horizontal gridlines: echarts' default splitLine is a light grey
  // that reads as near-white on the dark background and drowns out the data.
  const gridLineColor = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';

  // Process temperature data based on the unit
  const displayTempData = useMemo(() => {
    const sorted = [...temperatureData].sort((a, b) => new Date(a.time) - new Date(b.time));
    if (!isFahrenheit) {
      return sorted;
    } else {
      const fSorted = sorted.map((item) => ({
        ...item,
        // Assuming the value key in your temperatureData is 'value' or similar
        // Adjust 'value' to the actual key name in your temperatureData objects
        temperature:
          item.temperature !== undefined
            ? (item.temperature * 1.8 + 32).toFixed(2)
            : item.temperature.toFixed(2),
      }));
      return fSorted;
    }
  }, [temperatureData, isFahrenheit]);


  useEffect(() => {
    const checkVisibility = async () => {
      // 1. Define the configuration for the features you want to check
      const config = [
        { key: 'cycleTelemetry', id: 4, setter: setIsCycleTelemetryVisible },
        { key: 'temperatureTelemetry', id: 3, setter: setIsTemperatureTelemetryVisible },
        { key: 'impactTelemetry', id: 6, setter: setIsImpactTelemetryVisible },
      ];

      for (const { key, id, setter } of config) {
        const feature = features[key];

        // Case 1: Feature is off entirely
        if (!feature.isfeature) {
          setter(false);
          continue;
        }

        // Case 2: Feature is on and NOT device-specific (Show it immediately)
        if (!feature.isDeviceFeature) {
          setter(true);
          continue;
        }

        // Case 3: Feature is device-specific. Check all asset IDs.
        try {
          const requests = assetIds.map((assetId) => FeaturesService.getDeviceFeature(assetId, id));
          const responses = await Promise.all(requests);

          // Every device must have the feature for it to be visible
          const allDevicesHaveFeature = responses.every((res) => res.data.data.has_feature);
          setter(allDevicesHaveFeature);
        } catch (err) {
          console.error(`Failed to fetch Device Feature for ${key}`, err);
          setter(false); // Hide on error as a safety fallback
        }
      }

      // Trigger data fetch if dates are present
      if (startDate && endDate) {
        getData();
      }
    };

    checkVisibility();
  }, [startDate, endDate, features, assetIds]); // Added missing dependencies

  async function getData() {
    setLoading(true);
    try {
      const from = startDate;
      const to = endDate;

      // 2. Clean Await usage
      const response = await AssetsService.locationHistoryMechanical(assetIds, from, to);
      const result = response?.data?.data;
      console.log('result', result);

      if (!result) {
        // setLoadingText('No Data');
        return;
      }
      if (result.impactData) {
        setImpactData(result.impactData);
      }
      // Always set temperature if it exists to avoid repeating it in every 'if' block
      if (result.temperatureData) {
        setTemperatureData(result.temperatureData);
      }
      if (result.cyclesData?.length) {
        setCyclesData(result.cyclesData);
      } else if (!result.temperatureData?.length) {
        // If no history, no cycles, and no temperature
        // setCyclesData([]);
        // setLoadingText('No Data');
      }
    } catch (error) {
      console.error('Error fetching mechanical data:', error);
      // setLoadingText('Error loading data');
    } finally {
      setLoading(false); // 2. Stop loading regardless of success/error
    }
  }

  // Existing Binning Logic for aggregated charts @@@
  const processedData = useMemo(() => {
    if (!eventData || eventData.length === 0) return [];
    const bins = {};

    console.log("eventData",eventData)
    eventData.forEach((item) => {
      const m = moment(item.time);
      let binKey;
      if (grouping === 'Hourly') binKey = m.startOf('hour').format('YYYY-MM-DD HH:00:00');
      else if (grouping === 'Weekly') binKey = m.startOf('isoWeek').format('YYYY-MM-DD');
      else if (grouping === 'Monthly') binKey = m.startOf('month').format('YYYY-MM-DD');
      else binKey = m.startOf('day').format('YYYY-MM-DD');

      if (!bins[binKey]) {
        bins[binKey] = {
          time: binKey,
          shot_count: 0,
          cycle_period_sum: 0,
          cycle_period_count: 0,
          shot_count_total: 0,
        };
      }

      bins[binKey].shot_count += Number(item.shot_count || 0);
      bins[binKey].shot_count_total = Math.max(
        bins[binKey].shot_count_total,
        Number(item.shot_count_total || 0)
      );

      if (item.cycle_period > 0) {
        bins[binKey].cycle_period_sum += Number(item.cycle_period);
        bins[binKey].cycle_period_count += 1;
      }
    });

    return Object.values(bins)
      .map((b) => ({
        ...b,
        cycle_period:
          b.cycle_period_count > 0 ? (b.cycle_period_sum / b.cycle_period_count).toFixed(2) : 0,
      }))
      .sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [eventData, grouping]);

  const temperatureDataByAsset = useMemo(() => {
    if (!displayTempData || displayTempData.length === 0) return {};

    const byAsset = {};
    displayTempData.forEach((item) => {
      const assetName = item.name;
      if (!byAsset[assetName]) byAsset[assetName] = [];
      byAsset[assetName].push(item);
    });

    // Sort each asset's data by time
    Object.values(byAsset).forEach((arr) =>
      arr.sort((a, b) => new Date(a.time) - new Date(b.time))
    );

    return byAsset;
  }, [displayTempData]);

  const processedDataByAsset = useMemo(() => {
    if (!eventData || eventData.length === 0) return {};

    const assetBins = {};

    eventData.forEach((item) => {
      const assetId = item.name; // <-- confirm this field name
      if (!assetBins[assetId]) assetBins[assetId] = {};

      const m = moment(item.time);
      let binKey;
      if (grouping === 'Hourly') binKey = m.startOf('hour').format('YYYY-MM-DD HH:00:00');
      else if (grouping === 'Weekly') binKey = m.startOf('isoWeek').format('YYYY-MM-DD');
      else if (grouping === 'Monthly') binKey = m.startOf('month').format('YYYY-MM-DD');
      else binKey = m.startOf('day').format('YYYY-MM-DD');

      if (!assetBins[assetId][binKey]) {
        assetBins[assetId][binKey] = {
          time: binKey,
          shot_count: 0,
          cycle_period_sum: 0,
          cycle_period_count: 0,
          shot_count_total: 0,
        };
      }

      const bin = assetBins[assetId][binKey];
      bin.shot_count += Number(item.shot_count || 0);
      bin.shot_count_total = Math.max(bin.shot_count_total, Number(item.shot_count_total || 0));
      if (item.cycle_period > 0) {
        bin.cycle_period_sum += Number(item.cycle_period);
        bin.cycle_period_count += 1;
      }
    });

    const result = {};
    Object.entries(assetBins).forEach(([assetId, bins]) => {
      result[assetId] = Object.values(bins)
        .map((b) => ({
          ...b,
          cycle_period:
            b.cycle_period_count > 0 ? (b.cycle_period_sum / b.cycle_period_count).toFixed(2) : 0,
        }))
        .sort((a, b) => new Date(a.time) - new Date(b.time));
    });

    return result;
  }, [eventData, grouping]);

  // Temperature Chart Option (Raw Data, no binning)
  const getTempOption = () => {
    const assetEntries = Object.entries(temperatureDataByAsset);
    const hasData = assetEntries.some(([, data]) => data.length > 0);
    const hasHumidity = assetEntries.some(([, data]) => data.some((d) => d.humidity != null));
    let lastDate = '';

    const tempSeries = assetEntries.map(([assetName, data]) => ({
      name: assetName,
      type: 'line',
      yAxisIndex: 0,
      data: data.map((d) => [d.time, d.temperature]),
      lineStyle: { width: 2, color: assetColorMap[assetName] ?? '#ccc' },
      itemStyle: { color: assetColorMap[assetName] ?? '#ccc' },
      showSymbol: false,
      sampling: 'lttb',
    }));

    const humiditySeries = hasHumidity
      ? assetEntries
          .map(([assetName, data]) => {
            const pts = data.filter((d) => d.humidity != null);
            if (pts.length === 0) return null;
            const baseColor = assetColorMap[assetName] ?? '#ccc';
            return {
              name: `${assetName} Humidity`,
              type: 'line',
              yAxisIndex: 1,
              data: pts.map((d) => [d.time, d.humidity]),
              lineStyle: { width: 2, color: baseColor, type: 'dashed', opacity: 0.7 },
              itemStyle: { color: baseColor },
              showSymbol: false,
              sampling: 'lttb',
            };
          })
          .filter(Boolean)
      : [];

    const yAxis = hasHumidity
      ? [
          { type: 'value', name: `Temperature [${tempUnit}]`, scale: true, axisLabel: { color: axisTextColor }, axisLine: { lineStyle: { color: axisTextColor } }, nameTextStyle: { color: axisTextColor }, splitLine: { lineStyle: { color: gridLineColor } } },
          {
            type: 'value',
            name: 'Humidity [%]',
            scale: true,
            position: 'right',
            axisLine: { show: true, lineStyle: { color: axisTextColor } },
            axisLabel: { formatter: '{value}%', color: axisTextColor },
            nameTextStyle: { padding: [0, 0, 0, -90], color: axisTextColor },
            splitLine: { show: false },
          },
        ]
      : { type: 'value', name: `Temperature [${tempUnit}]`, scale: true, axisLabel: { color: axisTextColor }, axisLine: { lineStyle: { color: axisTextColor } }, nameTextStyle: { color: axisTextColor }, splitLine: { lineStyle: { color: gridLineColor } } };

    return {
      title: { text: 'Temperature', left: 'center', textStyle: { fontSize: 14, color: axisTextColor }, top: 4 },
      graphic: !hasData
        ? {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: { text: 'No Data Available', fill: noDataColor, font: '14px sans-serif' },
          }
        : [],
      toolbox: {
        right: 10,
        feature: { restore: { title: 'Reset' }, saveAsImage: { title: 'Save', name: 'Telemetry' } },
      },
      grid: { left: 60, right: hasHumidity ? 70 : 30, top: 40, bottom: 100 },
      legend: {
        bottom: 55,
        type: 'scroll',
        textStyle: { color: axisTextColor },
        formatter: (name) => {
          if (name.endsWith(' Humidity')) return `${name} (Humidity %)`;
          return `${name} (Temperature ${tempUnit})`;
        },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const timeHeader = moment(params[0].axisValue).format('MMM DD HH:mm');
          let res = `<b>${timeHeader}</b><br/>`;
          params.forEach((item) => {
            if (item.seriesName.endsWith(' Humidity')) {
              res += `${item.marker} ${item.seriesName}: <b>${item.value[1]}%</b><br/>`;
            } else {
              res += `${item.marker} ${item.seriesName}: <b>${item.value[1]} ${tempUnit}</b><br/>`;
            }
          });
          return res;
        },
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          hideOverlap: true,
          color: axisTextColor,
          formatter: (value) => {
            const m = moment(value);
            const dateStr = m.format('MMM DD');
            const timeStr = m.format('HH:mm');
            if (dateStr !== lastDate) {
              lastDate = dateStr;
              return timeStr === '00:00' ? dateStr : `${dateStr}\n${timeStr}`;
            }
            return timeStr;
          },
        },
        axisLine: { lineStyle: { color: axisTextColor } },
        splitNumber: 6,
      },
      yAxis,
      dataZoom: [
        { show: true, realtime: true, xAxisIndex: [0] },
        { type: 'inside', realtime: true, xAxisIndex: [0] },
      ],
      series: [...tempSeries, ...humiditySeries],
    };
  };

  const getImpactOption = () => {
    const hasData = impactData && impactData.length > 0;
    let lastDate = '';
    console.log('impactData', impactData);
    return {
      title: { text: 'Impact', left: 'center', textStyle: { fontSize: 14, color: axisTextColor }, top: 4 },
      graphic: !hasData
        ? {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: {
              text: 'No Data Available',
              fill: noDataColor,
              font: '14px sans-serif',
            },
          }
        : [],
      toolbox: {
        right: 10,
        feature: {
          restore: { title: 'Reset' },
          saveAsImage: {
            title: 'Save',
            name: 'Telemetry',
          },
        },
      },
      grid: { left: 60, right: 30, top: 40, bottom: 75 },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          // 1. Get the time from the first item in the axis stack
          const timeHeader = moment(params[0].axisValue).format('MMM DD HH:mm:ss');

          let res = `<b>${timeHeader}</b><br/>`;

          params.forEach((item) => {
            // 2. Access max_g from the value object
            const val = item.value.max_g;

            // 3. Display marker and value with 'mG' unit
            if (val !== undefined) {
              res += `${item.marker} Impact: <b>${val} mG</b>`;
            }
          });

          return res;
        },
      },
      dataset: {
        source: impactData.sort((a, b) => new Date(a.time) - new Date(b.time)),
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          hideOverlap: true,
          color: axisTextColor,
          formatter: (value) => {
            const m = moment(value);
            const dateStr = m.format('MMM DD');
            const timeStr = m.format('HH:mm');

            // logic: If it's the start of a day (00:00) or the date changed since the last tick
            if (dateStr !== lastDate) {
              lastDate = dateStr;
              // When zoomed out, show 'Dec 12'.
              // When zoomed in, show 'Dec 12 00:00' to mark the day break.
              return timeStr === '00:00' ? dateStr : `${dateStr}\n${timeStr}`;
            }

            // If the date hasn't changed, only show the time
            // This prevents repeating "Dec 12" over and over
            return timeStr;
          },
        },
        axisLine: { lineStyle: { color: axisTextColor } },
        // Optional: splitNumber helps ECharts decide how many ticks to try and draw
        splitNumber: 6,
      },
      yAxis: { type: 'value', name: `Force [mG]`, scale: true, axisLabel: { color: axisTextColor }, axisLine: { lineStyle: { color: axisTextColor } }, nameTextStyle: { color: axisTextColor }, splitLine: { lineStyle: { color: gridLineColor } } },
      dataZoom: [
        { show: true, realtime: true, xAxisIndex: [0] },
        { type: 'inside', realtime: true, xAxisIndex: [0] },
      ],
      series: [
        {
          // name: 'Temperature',
          type: 'line',
          encode: {
            x: 'time', // Matches the key for the X-axis
            y: 'max_g', // <--- CHANGE THIS to your data key (e.g., 'impact', 'temp')
          },
          lineStyle: { width: 2, color: '#FEC002' },
          itemStyle: { color: '#FEC002' },
          showSymbol: false,
          sampling: 'lttb',
          // --- Added professional areaStyle below ---
          // areaStyle: {
          //   color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          //     {
          //       offset: 0,
          //       color: 'rgba(249, 221, 101, 0.4)', // Your yellow color with 40% opacity at top
          //     },
          //     {
          //       offset: 1,
          //       color: 'rgba(249, 221, 101, 0)', // Fades to completely transparent at bottom
          //     },
          //   ]),
          // },
          // ------------------------------------------
        },
      ],
    };
  };

  const stringToColor = (str) => {
    if (!str) return '#ccc';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      let value = (hash >> (i * 8)) & 0xff;
      color += ('00' + value.toString(16)).slice(-2);
    }
    return color;
  };

  const transformSpaceData = (rawData) => {
    const sorted = [...rawData].sort((a, b) => new Date(a.time) - new Date(b.time));

    return sorted
      .map((item, index) => {
        const startTime = new Date(item.time).getTime();
        const nextItem = sorted[index + 1];
        const endTime = nextItem ? new Date(nextItem.time).getTime() : startTime;

        return {
          // FIX: Set the name property to the joined space_name
          // If space_name is null (no match in DB), it falls back to space_id
          name: item.space_name || item.space_id,
          value: [0, startTime, endTime, item.max_g],
          itemStyle: {
            // Use the name for color generation so the color stays consistent with the label
            color: stringToColor(item.space_name || item.space_id),
          },
        };
      })
      .filter((item) => item.value[2] > 0);
  };

  const getSpaceOption = () => {
    let lastDate = '';
    const data = transformSpaceData(impactData);

    // 1. Calculate the data bounds dynamically (matches Impact Chart behavior)
    const timestamps = impactData.map((d) => new Date(d.time).getTime());
    const dataMin = timestamps.length > 0 ? Math.min(...timestamps) : null;
    const dataMax = timestamps.length > 0 ? Math.max(...timestamps) : null;

    return {
      title: { text: 'Location', left: 'center', textStyle: { fontSize: 14, color: axisTextColor }, top: 4 },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          // 1. Get the name we set (now space_name)
          console.log('params', params);
          const spaceName = params.name;

          // 3. Get the max_g value we stored at index 3
          const force = params.value[3];

          return `
            <div style="border-bottom: 1px solid rgba(255,255,255,.3); font-size: 14px; padding-bottom: 7px; margin-bottom: 7px">
              <b>${spaceName}</b>
            </div>
            <span style="color: #FEC002">●</span> Max Impact: <b>${force} mG</b><br/>
          `;
        },
      },
      grid: { left: 60, right: 30, top: 40, bottom: 75 },
      xAxis: {
        type: 'time',
        // 2. Use the data-driven bounds instead of the global filter dates
        min: dataMin,
        max: dataMax,
        axisLabel: {
          hideOverlap: true,
          color: axisTextColor,
          formatter: (value) => {
            const m = moment(value);
            const dateStr = m.format('MMM DD');
            const timeStr = m.format('HH:mm');

            if (dateStr !== lastDate) {
              lastDate = dateStr;
              return timeStr === '00:00' ? dateStr : `${dateStr}\n${timeStr}`;
            }
            return timeStr;
          },
        },
        axisLine: { lineStyle: { color: axisTextColor } },
      },
      yAxis: { show: false, min: 0, max: 1 },
      dataZoom: [
        { show: true, realtime: true, xAxisIndex: [0] },
        { type: 'inside', realtime: true, xAxisIndex: [0] },
      ],
      series: [
        {
          type: 'custom',
          renderItem: (params, api) => {
            const start = api.coord([api.value(1), 0]);
            const end = api.coord([api.value(2), 0]);
            const height = 40;

            return {
              type: 'rect',
              shape: echarts.graphic.clipRectByRect(
                {
                  x: start[0],
                  y: start[1] - height / 2,
                  width: Math.max(end[0] - start[0], 1),
                  height: height,
                },
                {
                  x: params.coordSys.x,
                  y: params.coordSys.y,
                  width: params.coordSys.width,
                  height: params.coordSys.height,
                }
              ),
              style: api.style(),
            };
          },
          encode: { x: [1, 2], y: 0 },
          data: data,
        },
      ],
    };
  };

  // const ASSET_COLORS = ['#f9dd65', '#65b8f9', '#f965a0', '#65f9a0', '#f9a065'];

  const getOption = (yField, yName) => {
    const assetEntries = Object.entries(processedDataByAsset);
    const hasData = assetEntries.some(([, bins]) => bins.length > 0);

    let ylabel, title;
    const chartType = yField === 'cycle_period' ? 'line' : 'bar';
    if (yField === 'shot_count') {
      ylabel = 'Cycles';
      title = 'Cycles';
    } else if (yField === 'cycle_period') {
      ylabel = 'Cycle Time [s]';
      title = 'Cycle Time';
    }

    // All unique time keys across all assets
    const allTimes = [
      ...new Set(assetEntries.flatMap(([, bins]) => bins.map((b) => b.time))),
    ].sort();

    const series = assetEntries.map(([assetId, bins], i) => {
      // Map bins into a lookup for quick access by time
      const binByTime = Object.fromEntries(bins.map((b) => [b.time, b]));

      return {
        type: chartType,
        name: assetId,
        // color: ASSET_COLORS[i % ASSET_COLORS.length],
        color: assetColorMap[assetId] ?? '#ccc',
        smooth: true,
        data: allTimes.map((t) => binByTime[t]?.[yField] ?? null),
        label: {
          show: false,
          position: 'top',
          distance: 10,
          color: axisTextColor,
          fontSize: 12,
          formatter: (params) => params.value ?? '',
        },
      };
    });

    return {
      title: { text: title, left: 'center', textStyle: { fontSize: 14, color: axisTextColor }, top: 4 },
      graphic: !hasData
        ? {
            type: 'text',
            left: 'center',
            top: 'middle',
            style: { text: 'No Data Available', fill: noDataColor, font: '14px sans-serif' },
          }
        : [],
      legend: { bottom: 55, type: 'scroll', textStyle: { color: axisTextColor } },
      toolbox: { right: 10, feature: { saveAsImage: { title: 'Save', name: 'Telemetry' } } },
      grid: { left: 60, right: 30, top: 40, bottom: 100 },
      tooltip: { trigger: 'axis' },
      xAxis: {
        type: 'category',
        data: allTimes,
        axisLabel: {
          color: axisTextColor,
          rich: {
            month: { fontWeight: 'bold', color: axisTextColor },
          },
          formatter: (val) => {
            if (grouping === 'Hourly') {
              const m = moment(val);
              return `${m.format('DD')} {month|${m.format('MMM')}} ${m.format('HH:mm')}`;
            }
            return moment(val).format('MMM DD');
          },
        },
        axisLine: { lineStyle: { color: axisTextColor } },
      },
      yAxis: { type: 'value', name: ylabel, scale: true, axisLabel: { color: axisTextColor }, axisLine: { lineStyle: { color: axisTextColor } }, nameTextStyle: { color: axisTextColor }, splitLine: { lineStyle: { color: gridLineColor } } },
      dataZoom: [
        { show: true, realtime: true, xAxisIndex: [0] },
        { type: 'inside', realtime: true, xAxisIndex: [0] },
      ],
      series,
    };
  };

  // const getOption = (yField, yName, color) => {
  //   const hasData = processedData && processedData.length > 0;
  //   let ylabel = yName;
  //   let title;
  //   let chartType = yField === 'cycle_period' ? 'line' : 'bar';
  //   if (yField === 'shot_count') {
  //     ylabel = 'Cycles';
  //     title = 'Cycles';
  //   } else if (yField === 'cycle_period') {
  //     ylabel = 'Cycle Time [s]';
  //     title = 'Cycle Time';
  //   }

  //   return {
  //     title: { text: title, left: 'center', textStyle: { fontSize: 14 }, top: 4 },
  //     graphic: !hasData
  //       ? {
  //           type: 'text',
  //           left: 'center',
  //           top: 'middle',
  //           style: {
  //             text: 'No Data Available',
  //             fill: '#999',
  //             font: '14px sans-serif',
  //           },
  //         }
  //       : [],
  //     toolbox: {
  //       right: 10,
  //       feature: {
  //         saveAsImage: {
  //           title: 'Save',
  //           name: 'Telemetry', // MUST match the other chart exactly
  //         },
  //       },
  //     },
  //     grid: { left: 60, right: 30, top: 40, bottom: 75 },
  //     tooltip: { trigger: 'axis' },
  //     dataset: { source: processedData },
  //     xAxis: {
  //       type: 'category',
  //       axisLabel: {
  //         formatter: (val) => moment(val).format(grouping === 'Hourly' ? 'HH:mm' : 'MMM DD'),
  //       },
  //     },
  //     yAxis: { type: 'value', name: ylabel, scale: true },
  //     dataZoom: [
  //       { show: true, realtime: true, xAxisIndex: [0] },
  //       { type: 'inside', realtime: true, xAxisIndex: [0] },
  //     ],
  //     series: [
  //       {
  //         type: chartType,
  //         color: color,
  //         encode: { x: 'time', y: yField },
  //         smooth: true,
  //         label: {
  //           show: true, // Always show the label
  //           position: 'top', // Position it above the bar/point
  //           distance: 10, // Distance from the bar
  //           color: '#333', // Text color
  //           fontSize: 12,
  //           formatter: (params) => {
  //             // params.value is the data row; access the yField value specifically
  //             return params.value[yField];
  //           },
  //         },
  //       },
  //     ],
  //   };
  // };

  // Keep ALL Charts Connected (including the new Temp chart)
  useEffect(() => {
    const allRefs = [tempChartRef, impactChartRef, impactSpaceChartRef, ...chartRefs];
    const instances = allRefs.map((r) => r.current?.getEchartsInstance()).filter(Boolean);

    if (instances.length === 0) return;

    const GROUP_ID = 'mechanical_sync';
    instances.forEach((chart) => {
      chart.group = GROUP_ID;
    });

    echarts.connect(GROUP_ID);
    return () => echarts.disconnect(GROUP_ID);
  }, [processedData, displayTempData, chartRefs]); // Re-connect if data changes

  const chartsConfig = [
    // { field: 'shot_count_total', label: 'Total Shot Count', color: '#4CAF50' },
    { field: 'shot_count', label: 'Shot Count', color: '#f9dd65' },
    { field: 'cycle_period', label: 'Cycle Time [s]', color: '#f9dd65' },
  ];

  const chartContainerStyle = {
    flex: 1,
    background: 'var(--app-surface)',
    padding: '0px',
    borderRadius: '8px',
    border: '1px solid var(--app-border)',
  };

  return (
    <>
      <div ref={containerRef} style={{ position: 'relative', height: '100%' }}>
        <LoadingPanel2
          shadingColor="rgba(0,0,0,0.4)"
          visible={loading}
          showIndicator={true}
          shading={true}
          showPane={true}
          hideOnOutsideClick={false}
        />
      </div>
      <div className="cycles-container">
        <div className="cycles-grouping-container">
          <SelectBox
            style={{ width: '130px' }}
            dataSource={['Hourly', 'Daily', 'Weekly', 'Monthly']}
            value={grouping}
            onValueChanged={(e) => setGrouping(e.value)}
            label="Grouping"
            stylingMode="outlined"
          />
        </div>
        <div
          style={{
            width: 'calc(100% - 150px)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {/* 1. Impact Chart - Now a direct child */}
          {console.log("isImpactTelemetryVisible",isImpactTelemetryVisible)}
          {isImpactTelemetryVisible && (
            <div style={chartContainerStyle}>
              <ReactECharts
                ref={impactChartRef}
                option={getImpactOption()}
                style={{ height: '100%', width: '100%' }}
                notMerge={true}
              />
            </div>
          )}

          {/* 2. Space Timeline - Now a direct child */}
          {isImpactTelemetryVisible && (
            <div style={chartContainerStyle}>
              <ReactECharts
                ref={impactSpaceChartRef}
                option={getSpaceOption()}
                style={{ height: '100%', width: '100%' }}
                notMerge={true}
              />
            </div>
          )}

          {/* 3. Temperature Chart */}
          {isTemperatureTelemetryVisible && (
            <div style={chartContainerStyle}>
              <ReactECharts
                ref={tempChartRef}
                option={getTempOption()}
                style={{ height: '100%', width: '100%' }}
                notMerge={true}
              />
            </div>
          )}

          {/* 4. Cycle Charts - Every map item is a direct child */}
          {isCycleTelemetryVisible &&
            chartsConfig.map((cfg, index) => (
              <div key={cfg.field} style={chartContainerStyle}>
                <ReactECharts
                  ref={chartRefs[index]}
                  option={getOption(cfg.field, cfg.label)}
                  style={{ height: '100%', width: '100%' }}
                  notMerge={true}
                />
              </div>
            ))}
        </div>
      </div>
    </>
  );
}
